import React, { useMemo } from 'react';
import StockChart from './StockChart';
import FinancialMetrics from './FinancialMetrics';
import { WsbSlotMachineBackground } from './WsbSlotMachineBackground';
import { StockData, ChartDataPoint, NewsEvent } from '../../types/game';
import { StockCategoryFilter } from '../../types/stockCategories';
import { cn } from '../../utils/cn';

interface GameMainContentProps {
  stockData?: StockData | null;
  chartData?: ChartDataPoint[];
  stockDataLoading: boolean;
  category?: StockCategoryFilter;
  currentCategory: StockCategoryFilter;
  isReplayMode?: boolean;
  financialMetrics?: Record<string, string | null> | null;
  selectedGroups?: [string, string] | null;
  financialMetricsLoading: boolean;
  financialMetricsError?: string | null;
  visibleHints?: number;
  metricsRef?: React.RefObject<HTMLElement | null>;
  className?: string;
  spotlightStep?: string | null;
  isStreamerMode?: boolean;
  newsEvents?: NewsEvent[] | null;
}

const EMPTY_METRICS_MAP: Record<string, string | null> = {};

/**
 * GameMainContent component - handles the main game layout
 * Includes stock chart (left) and financial metrics (right) in a responsive grid
 */
const GameMainContentComponent: React.FC<GameMainContentProps> = ({
  stockData,
  chartData,
  stockDataLoading,
  category,
  currentCategory,
  financialMetrics,
  selectedGroups,
  financialMetricsLoading,
  financialMetricsError,
  visibleHints = 0,
  metricsRef,
  className,
  spotlightStep,
  isReplayMode = false,
  isStreamerMode,
  newsEvents,
}) => {
  const metricsMap = useMemo(() => {
    return financialMetrics ?? EMPTY_METRICS_MAP;
  }, [financialMetrics]);

  // Memoize derived values to prevent unnecessary recalculations
  const activeCategory = useMemo(() => category || currentCategory, [category, currentCategory]);
  const isWallstreetbetsCategory = activeCategory === 'wallstreetbets';

  const hasMetricsValues = useMemo(() => {
    return Boolean(
      financialMetrics &&
      Object.values(financialMetrics).some(value => value !== null)
    );
  }, [financialMetrics]);

  const shouldShowMetricsError = useMemo(() => {
    return Boolean(
      financialMetricsError &&
      !financialMetricsLoading &&
      !hasMetricsValues
    );
  }, [financialMetricsError, financialMetricsLoading, hasMetricsValues]);

  const showMetricsLoading = useMemo(() => {
    return financialMetricsLoading && !hasMetricsValues;
  }, [financialMetricsLoading, hasMetricsValues]);

  // Memoize layout calculations
  const showPrice = visibleHints >= 2;
  const showDate = visibleHints >= 3;
  const visibleNewsCount = visibleHints >= 4 ? 2 : visibleHints >= 3 ? 1 : 0;

  const metricsLayout = 'mobile-grid' as const;

  // Log chart data passing for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 [GameMainContent] Passing chart data to StockChart component:', {
      propName: 'data',
      propValue: chartData,
      propType: typeof chartData,
      propLength: Array.isArray(chartData) ? chartData.length : 'not array',
      isValidArray: Array.isArray(chartData) && chartData.length > 0,
      loadingProp: stockDataLoading,
      category: activeCategory,
      stockSymbol: stockData?.Symbol,
      timestamp: new Date().toISOString(),
      sampleData: Array.isArray(chartData) ? chartData.slice(0, 2) : 'not array'
    });

    console.log('💰 [GameMainContent] Financial metrics state:', {
      category: activeCategory,
      metricsLoading: financialMetricsLoading,
      metricsError: financialMetricsError,
      hasMetrics: !!financialMetrics,
      selectedGroups,
    });
  }

  return (
    <div className={cn('space-y-0', className)}>
      <section className="space-y-3" data-spotlight-id="price-chart">
        <div className="relative">
          {isWallstreetbetsCategory && <WsbSlotMachineBackground />}
          <StockChart
            data={chartData}
            loading={stockDataLoading}
            backgroundClassName={isWallstreetbetsCategory ? 'relative isolate' : undefined}
            showPrice={showPrice}
            showDate={showDate}
            category={activeCategory}
            isReplayMode={isReplayMode}
            newsEvents={newsEvents}
            visibleNewsCount={visibleNewsCount}
          />
        </div>
      </section>

      <section ref={metricsRef} data-tutorial-id="financial-metrics" className="space-y-3">
        {shouldShowMetricsError ? (
          <div className="space-y-4">
            <FinancialMetrics
              metricsMap={EMPTY_METRICS_MAP}
              selectedGroups={selectedGroups}
              loading={false}
              layout={metricsLayout}
              visibleHints={visibleHints}
              category={activeCategory}
              spotlightStep={spotlightStep}
              isStreamerMode={isStreamerMode}
            />
            <div className="text-sm text-muted-foreground px-2">
              {financialMetricsError}
            </div>
          </div>
        ) : (
          <FinancialMetrics
            metricsMap={metricsMap}
            selectedGroups={selectedGroups}
            loading={showMetricsLoading}
            layout={metricsLayout}
            visibleHints={visibleHints}
            category={activeCategory}
            spotlightStep={spotlightStep}
            isStreamerMode={isStreamerMode}
          />
        )}
      </section>
    </div>
  );
};

/**
 * Custom comparison function for React.memo to prevent unnecessary re-renders
 */
const arePropsEqual = (
  prevProps: GameMainContentProps,
  nextProps: GameMainContentProps
): boolean => {
  if (
    prevProps.stockDataLoading !== nextProps.stockDataLoading ||
    prevProps.financialMetricsLoading !== nextProps.financialMetricsLoading ||
    prevProps.financialMetricsError !== nextProps.financialMetricsError ||
    prevProps.category !== nextProps.category ||
    prevProps.currentCategory !== nextProps.currentCategory ||
    prevProps.visibleHints !== nextProps.visibleHints ||
    prevProps.metricsRef !== nextProps.metricsRef ||
    prevProps.className !== nextProps.className ||
    prevProps.spotlightStep !== nextProps.spotlightStep ||
    prevProps.selectedGroups !== nextProps.selectedGroups
  ) {
    return false;
  }

  if (prevProps.stockData !== nextProps.stockData) {
    if (!prevProps.stockData && !nextProps.stockData) {
      // Continue
    } else if (!prevProps.stockData || !nextProps.stockData) {
      return false;
    } else {
      if (
        prevProps.stockData.Symbol !== nextProps.stockData.Symbol ||
        prevProps.stockData.Security !== nextProps.stockData.Security
      ) {
        return false;
      }
    }
  }

  if (prevProps.chartData !== nextProps.chartData) {
    if (!prevProps.chartData && !nextProps.chartData) {
      // Continue
    } else if (!prevProps.chartData || !nextProps.chartData) {
      return false;
    } else {
      if (prevProps.chartData.length !== nextProps.chartData.length) {
        return false;
      }
    }
  }

  if (prevProps.financialMetrics !== nextProps.financialMetrics) {
    if (!prevProps.financialMetrics && !nextProps.financialMetrics) {
      // Continue
    } else if (!prevProps.financialMetrics || !nextProps.financialMetrics) {
      return false;
    }
  }

  return true;
};

export const GameMainContent = React.memo(GameMainContentComponent, arePropsEqual);
